import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";

const SECTIONS = [
  { id: "hero", label: "Hero", num: "01" },
  { id: "about", label: "About", num: "02" },
  { id: "currently", label: "Work", num: "03" },
  { id: "expertise-ch", label: "Skills", num: "04" },
  { id: "projects-ch", label: "Projects", num: "05" },
  { id: "honours-ch", label: "Honours", num: "06" },
  { id: "blogs-ch", label: "Blogs", num: "07" },
  { id: "contact-ch", label: "Contact", num: "08" },
];

const SectionNav: React.FC = () => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("hero");
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  const isHome = pathname === "/";

  useEffect(() => {
    if (!isHome) return;

    const handleScroll = () => {
      setVisible(window.scrollY > 200);

      // Page scroll progress
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docH > 0 ? window.scrollY / docH : 0);

      const vh = window.innerHeight;
      let current = "hero";
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top < vh * 0.5) current = s.id;
        }
      }
      setActive(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  if (!isHome) return null;

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeIdx = SECTIONS.findIndex(s => s.id === active);

  return (
    <Nav $visible={visible}>
      {/* Progress track */}
      <TrackBg />
      <TrackFill style={{ height: `${progress * 100}%` }} />

      {SECTIONS.map((s, i) => {
        const isActive = active === s.id;
        const isPast = i < activeIdx;
        return (
          <NavItem key={s.id} onClick={() => scrollTo(s.id)} $active={isActive}>
            {/* Connector line to next dot */}
            {i < SECTIONS.length - 1 && <Connector $lit={isPast || isActive} />}

            <DotWrapper>
              {isActive && <DotRing />}
              <Dot $active={isActive} $past={isPast} />
            </DotWrapper>

            <LabelGroup $active={isActive}>
              <LabelNum $active={isActive}>{s.num}</LabelNum>
              <LabelText $active={isActive}>{s.label}</LabelText>
            </LabelGroup>
          </NavItem>
        );
      })}
    </Nav>
  );
};

export default SectionNav;

/* ── Animations ── */

const ringPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.6); opacity: 0; }
`;

const dotGlow = keyframes`
  0%, 100% { box-shadow: 0 0 4px rgba(255,215,0,0.3); }
  50% { box-shadow: 0 0 10px rgba(255,215,0,0.6), 0 0 20px rgba(255,215,0,0.15); }
`;

/* ── Styles ── */

const Nav = styled.nav<{ $visible: boolean }>`
  position: fixed;
  right: 22px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 90;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.5s ease;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};

  @media (max-width: 900px) { display: none; }
`;

/* Vertical progress track */
const TrackBg = styled.div`
  position: absolute;
  right: 5px;
  top: 6px;
  bottom: 6px;
  width: 1px;
  background: rgba(255, 215, 0, 0.06);
  border-radius: 1px;
  pointer-events: none;
`;

const TrackFill = styled.div`
  position: absolute;
  right: 5px;
  top: 6px;
  width: 1px;
  background: linear-gradient(180deg, #ffd700, rgba(255, 215, 0, 0.3));
  border-radius: 1px;
  pointer-events: none;
  transition: height 0.15s linear;
`;

const NavItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  cursor: pointer;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateX(-6px);
  }
`;

const Connector = styled.div<{ $lit: boolean }>`
  position: absolute;
  right: 5px;
  top: 50%;
  height: calc(100% + 0px);
  width: 1px;
  background: ${({ $lit }) => $lit ? "rgba(255, 215, 0, 0.2)" : "transparent"};
  pointer-events: none;
  z-index: -1;
`;

const DotWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
`;

const DotRing = styled.div`
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid #ffd700;
  animation: ${ringPulse} 2s ease-in-out infinite;
`;

const Dot = styled.div<{ $active: boolean; $past: boolean }>`
  width: ${({ $active }) => ($active ? "9px" : "5px")};
  height: ${({ $active }) => ($active ? "9px" : "5px")};
  border-radius: 50%;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  ${({ $active }) => $active && css`
    background: #ffd700;
    animation: ${dotGlow} 2s ease-in-out infinite;
  `}

  ${({ $active, $past }) => !$active && css`
    background: ${$past ? "rgba(255, 215, 0, 0.35)" : "rgba(255, 215, 0, 0.12)"};
    border: 1px solid ${$past ? "rgba(255, 215, 0, 0.25)" : "rgba(255, 215, 0, 0.1)"};
  `}

  ${NavItem}:hover & {
    background: rgba(255, 215, 0, 0.5);
    transform: scale(1.3);
  }
`;

const LabelGroup = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: baseline;
  gap: 5px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transform: translateX(${({ $active }) => ($active ? "0" : "6px")});
  transition: all 0.35s ease;
  pointer-events: none;

  ${NavItem}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const LabelNum = styled.span<{ $active: boolean }>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  font-weight: 700;
  color: ${({ $active }) => ($active ? "#ffd700" : "#5a4a3a")};
  transition: color 0.3s;

  ${NavItem}:hover & { color: #ffd700; }
`;

const LabelText = styled.span<{ $active: boolean }>`
  font-family: "Cinzel", serif;
  font-size: 0.52rem;
  font-weight: ${({ $active }) => ($active ? "700" : "500")};
  color: ${({ $active }) => ($active ? "#ffd700" : "#6a5a4a")};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  transition: color 0.3s;

  ${NavItem}:hover & { color: #e8dfd5; }
`;
